/**
 * Created: 2026-04-08
 * Initial commit: Refactor date navigation into DateNavigation component (feat/event/mr-10)
 *
 * Date picker with prev/next day buttons and a today shortcut.
 */

import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import MyLocationIcon from '@mui/icons-material/MyLocation'
import { Box, IconButton, InputAdornment, Tooltip } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { addDays, addMonths, subDays, subYears } from 'date-fns'
import { debounce } from 'lodash'
import { useEffect, useMemo, useState } from 'react'

interface DateNavigationProps {
  value: Date
  onChange: (date: Date) => void
}

export default function DateNavigation({ value, onChange }: DateNavigationProps) {
  const today = useMemo(() => new Date(), [])
  const minDate = subYears(today, 1)
  const maxDate = addMonths(today, 3)

  const [inputDate, setInputDate] = useState<Date | null>(value)

  const debouncedOnChange = useMemo(
    () => debounce((date: Date) => onChange(date), 300),
    [onChange],
  )

  useEffect(() => {
    return () => {
      debouncedOnChange.cancel()
    }
  }, [debouncedOnChange])

  const isPrevDisabled = !inputDate || subDays(inputDate, 1) < minDate
  const isNextDisabled = !inputDate || addDays(inputDate, 1) > maxDate

  const handleChange = (date: Date | null) => {
    setInputDate(date)
  }

  const handleAccept = (date: Date | null) => {
    if (date && !Number.isNaN(date.getTime()) && date.getFullYear() >= 2000) {
      setInputDate(date)
      onChange(date)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleAccept(inputDate)
  }

  const handlePrev = () => {
    if (!inputDate) return
    const prev = subDays(inputDate, 1)
    if (prev >= minDate) {
      setInputDate(prev)
      debouncedOnChange(prev)
    }
  }

  const handleNext = () => {
    if (!inputDate) return
    const next = addDays(inputDate, 1)
    if (next <= maxDate) {
      setInputDate(next)
      debouncedOnChange(next)
    }
  }

  const handleToday = () => {
    const now = new Date()
    setInputDate(now)
    debouncedOnChange(now)
  }

  return (
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      <Tooltip title="前一天">
        <span>
          <IconButton
            size="small"
            onClick={handlePrev}
            disabled={isPrevDisabled}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover',
              },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
        </span>
      </Tooltip>
      <DatePicker
        value={inputDate}
        onChange={handleChange}
        onAccept={handleAccept}
        format="yyyy-MM-dd E"
        minDate={minDate}
        maxDate={maxDate}
        slotProps={{
          textField: {
            size: 'small',
            onKeyDown: handleKeyDown,
            sx: { width: 220 },
            InputProps: {
              startAdornment: (
                <InputAdornment position="start">
                  <Tooltip title="今天">
                    <IconButton
                      size="small"
                      onClick={handleToday}
                      edge="start"
                      aria-label="go to today"
                    >
                      <MyLocationIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            },
          },
          actionBar: {
            actions: [],
            sx: {
              '& .MuiButton-root': {
                border: '1px solid',
                borderColor: 'primary.main',
              },
            },
          },
        }}
      />
      <Tooltip title="后一天">
        <span>
          <IconButton
            size="small"
            onClick={handleNext}
            disabled={isNextDisabled}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              '&:hover': {
                borderColor: 'primary.main',
                backgroundColor: 'action.hover',
              },
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  )
}
